import React from 'react';
import Icon from '../../../components/AppIcon';
import {
  resolveComponentBackground,
  resolveShapeRadius,
  resolveButtonStyle,
  resolveTextFillStyle,
  scrollToAnchor,
} from '../utils/defaults';
import {
  resolveIconStrokeColor,
  resolveColorFillCss,
} from '../utils/colorFillUtils';
import { resolvePublicPath } from '../utils/commerce';
import { normalizeIconName } from '../utils/iconCatalog';
import { buildHoverEffectPresentation } from '../utils/mediaHoverEffects';
import {
  isRichHtmlValue,
  richHtmlIsEmpty,
  plainTextToEditorHtml,
} from '../utils/cardRichText';
import '../styles/webCreatorCards.css';
import '../styles/webCreatorHoverEffects.css';
import '../styles/webCreatorRichText.css';

function CardRichText({ value, format, fallback, className = '', style }) {
  const raw = value ?? '';
  if (isRichHtmlValue(raw, format)) {
    if (richHtmlIsEmpty(raw)) {
      return fallback ? (
        <div className={`web-creator-rich-text m-0 opacity-60 ${className}`.trim()} style={style}>
          {fallback}
        </div>
      ) : null;
    }
    return (
      <div
        className={`web-creator-rich-text m-0 ${className}`.trim()}
        style={style}
        dangerouslySetInnerHTML={{ __html: raw }}
      />
    );
  }
  const text = String(raw || '').trim();
  if (!text) {
    return fallback ? (
      <div className={`m-0 ${className}`.trim()} style={style}>
        {fallback}
      </div>
    ) : null;
  }
  return (
    <div className={`m-0 whitespace-pre-wrap ${className}`.trim()} style={style}>
      {text}
    </div>
  );
}

function CardLinkLabel({ label, format, fallback, className = '' }) {
  const raw = label ?? '';
  if (isRichHtmlValue(raw, format)) {
    if (richHtmlIsEmpty(raw)) {
      return <span className={className}>{fallback}</span>;
    }
    return (
      <span
        className={`${className} web-creator-rich-text`.trim()}
        dangerouslySetInnerHTML={{ __html: raw }}
      />
    );
  }
  return <span className={className}>{raw || fallback}</span>;
}

function handleCardLink({ interactive, linkType, sectionAnchor, url, publicPath, checkoutSlug }) {
  if (!interactive) return;
  const type = linkType || 'section';
  if (type === 'url' && url) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  if (type === 'publicPath' && publicPath && checkoutSlug) {
    window.location.href = resolvePublicPath(checkoutSlug, publicPath);
    return;
  }
  if (type === 'section' && sectionAnchor) {
    scrollToAnchor(String(sectionAnchor).replace(/^#/, ''));
  }
}

function CardShell({ comp, children, className = '' }) {
  const { shape, borderRadius } = comp.props || {};
  const radius = resolveShapeRadius(shape, borderRadius);
  const bg = resolveComponentBackground(comp.props);
  const cardHover = buildHoverEffectPresentation(comp.props || {}, 'card');
  const applyCardHover = cardHover.effect !== 'none';

  return (
    <div
      className={[
        'w-full h-full min-h-0 flex flex-col overflow-hidden border border-border/70 shadow-sm',
        applyCardHover ? cardHover.selfClass : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        borderRadius: `${radius}px`,
        ...bg,
        ...(applyCardHover ? cardHover.style : {}),
      }}
    >
      {children}
    </div>
  );
}

function CardMediaSlot({ side, props }) {
  const prefix = side === 'left' ? 'left' : 'right';
  const mediaType = props[`${prefix}MediaType`] || (side === 'left' ? 'icon' : 'none');
  if (mediaType === 'none') return null;

  if (mediaType === 'image') {
    const url = props[`${prefix}ImageUrl`];
    if (!url) return null;
    return (
      <img
        src={url}
        alt=""
        className="h-9 w-9 object-contain shrink-0"
      />
    );
  }

  const name = normalizeIconName(props[`${prefix}IconName`] || 'Star');
  const size = Math.max(16, Math.min(48, Number(props[`${prefix}IconSize`]) || 28));
  const color = resolveIconStrokeColor(props[`${prefix}IconColor`], '#2563eb');
  return <Icon name={name} size={size} color={color} strokeWidth={2} className="shrink-0" />;
}

export function CardFeatureView({ comp, interactive, checkoutSlug }) {
  const p = comp.props || {};
  const titleStyle = {
    ...resolveTextFillStyle(p.titleColor, '#0f172a'),
    fontSize: p.titleFontSize ? `${p.titleFontSize}px` : '1.125rem',
    fontWeight: p.titleFontWeight ?? 600,
    lineHeight: 1.3,
  };
  const descStyle = {
    ...resolveTextFillStyle(p.descriptionColor, '#64748b'),
    fontSize: p.descriptionFontSize ? `${p.descriptionFontSize}px` : '0.875rem',
    lineHeight: 1.55,
  };
  const linkStyle = resolveTextFillStyle(p.linkColor, '#2563eb');
  const linkHoverCss = resolveColorFillCss(
    p.linkHoverColor,
    resolveColorFillCss(p.linkColor, '#2563eb'),
  );
  const linkHover = buildHoverEffectPresentation(p, 'link');
  const applyLinkHover = linkHover.effect !== 'none';

  const onLink = () =>
    handleCardLink({
      interactive,
      linkType: p.linkType || 'section',
      sectionAnchor: p.sectionAnchor,
      url: p.url,
      publicPath: p.publicPath,
      checkoutSlug,
    });

  const linkCssVars = {
    '--wc-card-link-color': resolveColorFillCss(p.linkColor, '#2563eb'),
    '--wc-card-link-hover': linkHoverCss,
  };

  const linkInner = (
    <>
      <CardLinkLabel
        className="wc-card-link-label"
        label={p.linkLabel}
        format={p.linkLabelFormat}
        fallback="En savoir plus"
      />
      <span className="wc-card-link-arrow text-base leading-none" aria-hidden>
        →
      </span>
    </>
  );

  const linkClassName = [
    'wc-card-feature-link inline-flex items-center gap-2 p-0 border-0 bg-transparent text-left w-fit mt-auto',
    interactive ? 'cursor-pointer' : '',
    applyLinkHover ? linkHover.selfClass : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <CardShell comp={comp}>
      <div className="flex flex-col flex-1 min-h-0 p-5 sm:p-6 gap-4">
        <div className="flex items-start justify-between gap-3">
          <CardMediaSlot side="left" props={p} />
          <CardMediaSlot side="right" props={p} />
        </div>
        <CardRichText
          value={p.title}
          format={p.titleFormat}
          fallback="Titre de la carte"
          className="flex-shrink-0"
          style={titleStyle}
        />
        <CardRichText
          value={p.description}
          format={p.descriptionFormat}
          fallback="Description de la carte…"
          className="flex-1 min-h-0"
          style={descStyle}
        />
        {p.linkLabel !== '' && (p.linkType || p.linkLabel) ? (
          interactive ? (
            <button
              type="button"
              className={linkClassName}
              style={{
                ...linkCssVars,
                ...(applyLinkHover ? linkHover.style : {}),
                ...(!applyLinkHover ? linkStyle : {}),
              }}
              onClick={onLink}
            >
              {linkInner}
            </button>
          ) : (
            <span
              className={`${linkClassName}`.replace('cursor-pointer', '')}
              style={{ ...linkCssVars, ...(!applyLinkHover ? linkStyle : {}) }}
            >
              {linkInner}
            </span>
          )
        ) : null}
      </div>
    </CardShell>
  );
}

export function CardIllustrationView({ comp }) {
  const p = comp.props || {};
  const objectFit = p.imageObjectFit || 'cover';
  const imageHover = buildHoverEffectPresentation(p, 'image');
  const useImageWrap = imageHover.effect !== 'none' && imageHover.wrapClass;

  const titleStyle = {
    ...resolveTextFillStyle(p.titleColor, '#0f172a'),
    fontSize: p.titleFontSize ? `${p.titleFontSize}px` : '1.125rem',
    fontWeight: p.titleFontWeight ?? 600,
    lineHeight: 1.3,
  };
  const descStyle = {
    ...resolveTextFillStyle(p.descriptionColor, '#64748b'),
    fontSize: p.descriptionFontSize ? `${p.descriptionFontSize}px` : '0.875rem',
    lineHeight: 1.55,
  };

  const mediaInner = p.imageUrl ? (
    <img
      src={p.imageUrl}
      alt={p.imageAlt || ''}
      className={`wc-card-illustration-img ${useImageWrap ? 'wc-hover-fx-target' : ''}`.trim()}
      style={{ objectFit }}
    />
  ) : (
    <div
      className={`wc-card-illustration-img flex items-center justify-center bg-muted/40 text-xs text-muted-foreground ${useImageWrap ? 'wc-hover-fx-target' : ''}`.trim()}
      style={{ objectFit: 'cover' }}
    >
      Illustration
    </div>
  );

  return (
    <CardShell comp={comp} className="wc-card-illustration !p-0">
      <div
        className={[
          'wc-card-illustration__media',
          useImageWrap ? imageHover.wrapClass : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={useImageWrap ? imageHover.style : undefined}
      >
        {mediaInner}
      </div>
      <div className="wc-card-illustration__body flex flex-col gap-2 px-5 py-4 sm:px-6 sm:py-5">
        <CardRichText
          value={p.title}
          format={p.titleFormat}
          fallback="Titre"
          style={titleStyle}
        />
        <CardRichText
          value={p.description}
          format={p.descriptionFormat}
          fallback="Description…"
          style={descStyle}
        />
      </div>
    </CardShell>
  );
}

export function CardSplitView({
  comp,
  interactive,
  checkoutSlug,
  siteTypography,
  editorViewport,
}) {
  const p = comp.props || {};
  const stack = editorViewport === 'mobile' || editorViewport === 'tablet';
  const eyebrowStyle = {
    ...resolveTextFillStyle(p.eyebrowColor, '#64748b'),
    fontSize: p.eyebrowFontSize ? `${p.eyebrowFontSize}px` : '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  };
  const subtitleStyle = {
    ...resolveTextFillStyle(p.subtitleColor, '#475569'),
    fontSize: p.subtitleFontSize ? `${p.subtitleFontSize}px` : '0.875rem',
    lineHeight: 1.4,
  };
  const headingStyle = {
    ...resolveTextFillStyle(p.headingColor, '#0f172a'),
    fontSize: p.headingFontSize ? `${p.headingFontSize}px` : '1.5rem',
    fontWeight: p.headingFontWeight ?? 700,
    lineHeight: 1.2,
  };
  const descStyle = {
    ...resolveTextFillStyle(p.descriptionColor, '#64748b'),
    fontSize: p.descriptionFontSize ? `${p.descriptionFontSize}px` : '0.9375rem',
    lineHeight: 1.6,
  };

  const ctaProps = {
    label: p.buttonLabel,
    backgroundColor: p.buttonBackgroundColor,
    textColor: p.buttonTextColor,
    shape: p.buttonShape || 'rounded',
    borderRadius: p.buttonBorderRadius ?? 8,
    fontSize: p.buttonFontSize ?? 14,
    fontWeight: p.buttonFontWeight ?? 600,
    width: 'auto',
    inheritSiteButtonFont: p.inheritSiteButtonFont,
    buttonFontFamily: p.buttonFontFamily,
  };
  const { button: btnStyle, text: btnTextStyle } = resolveButtonStyle(ctaProps, siteTypography);
  const buttonHover = buildHoverEffectPresentation(p, 'button');
  const applyButtonHover = buttonHover.effect !== 'none';

  const onButton = () =>
    handleCardLink({
      interactive,
      linkType: p.buttonLinkType || 'section',
      sectionAnchor: p.buttonSectionAnchor,
      url: p.buttonUrl,
      publicPath: p.buttonPublicPath,
      checkoutSlug,
    });

  const textCol = (
    <div className="flex flex-col justify-center gap-3 p-5 sm:p-8 min-h-0 h-full">
      {!richHtmlIsEmpty(p.eyebrow) || (p.eyebrow && !isRichHtmlValue(p.eyebrow, p.eyebrowFormat)) ? (
        <CardRichText
          value={p.eyebrow}
          format={p.eyebrowFormat}
          style={eyebrowStyle}
        />
      ) : null}
      {!richHtmlIsEmpty(p.subtitle) || (p.subtitle && !isRichHtmlValue(p.subtitle, p.subtitleFormat)) ? (
        <CardRichText
          value={p.subtitle}
          format={p.subtitleFormat}
          style={subtitleStyle}
        />
      ) : null}
      <CardRichText
        value={p.heading}
        format={p.headingFormat}
        fallback="Titre principal"
        style={headingStyle}
      />
      <CardRichText
        value={p.description}
        format={p.descriptionFormat}
        fallback="Description…"
        className="max-w-prose"
        style={descStyle}
      />
      {p.buttonLabel !== '' ? (
        <button
          type="button"
          className={[
            'self-start px-5 py-2.5 shadow-sm mt-1',
            interactive ? 'cursor-pointer' : 'cursor-default',
            applyButtonHover ? buttonHover.selfClass : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            ...btnStyle,
            ...(applyButtonHover ? buttonHover.style : {}),
          }}
          onClick={interactive ? onButton : undefined}
        >
          {isRichHtmlValue(p.buttonLabel, p.buttonLabelFormat) ? (
            <span
              className="web-creator-rich-text"
              style={btnTextStyle}
              dangerouslySetInnerHTML={{
                __html: richHtmlIsEmpty(p.buttonLabel)
                  ? plainTextToEditorHtml('Action')
                  : p.buttonLabel,
              }}
            />
          ) : (
            <span style={btnTextStyle}>{p.buttonLabel || 'Action'}</span>
          )}
        </button>
      ) : null}
    </div>
  );

  const splitImageHover = buildHoverEffectPresentation(p, 'image');
  const splitImageWrap = splitImageHover.effect !== 'none' && splitImageHover.wrapClass;

  const splitMediaInner = p.imageUrl ? (
    <img
      src={p.imageUrl}
      alt={p.imageAlt || ''}
      className={`w-full h-full object-cover ${splitImageWrap ? 'wc-hover-fx-target' : ''}`.trim()}
    />
  ) : (
    <div
      className={`w-full h-full min-h-[200px] bg-muted/50 flex items-center justify-center text-sm text-muted-foreground ${splitImageWrap ? 'wc-hover-fx-target' : ''}`.trim()}
    >
      Image
    </div>
  );

  const mediaCol = (
    <div
      className={[
        'wc-card-split__media relative min-h-0 h-full flex items-stretch overflow-hidden',
        splitImageWrap ? splitImageHover.wrapClass : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={splitImageWrap ? splitImageHover.style : undefined}
    >
      {splitMediaInner}
    </div>
  );

  const gridClass = [
    'wc-card-split grid h-full min-h-0 flex-1',
    stack ? 'wc-card-split--stack' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <CardShell comp={comp} className="!p-0">
      <div className={gridClass}>
        {textCol}
        {mediaCol}
      </div>
    </CardShell>
  );
}
